import type { ModelVariable } from '../../api/models'

/**
 * The colour ramp, as a lookup table the GPU can sample — the browser half of
 * `palette_colors()` in `app/models/render.py`.
 *
 * Until Phase 7 the ramp was baked into the artifact: `quantize()` wrote an
 * index and `PLTE` was the lookup, so the colours arrived with the pixels. A
 * data texture carries the index and nothing else, so the lookup moves here
 * and the ramp is applied at draw time. What that buys is that **a colormap
 * edit takes effect without a re-render** — which is the argument that decided
 * the replace-versus-coexist question for the whole phase.
 *
 * **Two implementations of one ramp is the `GROUP_LABELS` bug**, so this one
 * is pinned rather than trusted: `scripts/refresh_web_fixtures.py` writes
 * `palette_colors()` output for representative fields into
 * `web/tests/fixtures/model_ramps.json`, and `ramp.test.ts` asserts
 * `buildRamp()` reproduces it **byte for byte**. After the paletted plate is
 * gone the Python side has exactly one remaining consumer —
 * `scripts/render_frame.py` — so that fixture is what keeps the debugging tool
 * and the app agreeing about what a colour means.
 *
 * Note what this buys and what it does not. A **colormap** edit is free. A
 * **`plotted`** edit still invalidates every stored run, because it moves the
 * quantisation — which is why the range a plate was quantised with travels in
 * the run's manifest rather than being read from the live catalog.
 */

/**
 * How a run's data texture decodes, straight out of its manifest.
 *
 * `value = lo + index / (levels - 1) * (hi - lo)`, and `index === nodata`
 * means nothing there.
 *
 * **From the manifest, never from the catalog.** A client that took `plotted`
 * from the live catalog and applied it to a plate quantised against the old
 * one would draw wrong numbers in right-looking colours — the one genuinely
 * new hazard the data texture introduces, and the reason this block exists at
 * all. A run is decoded with the range it was born with.
 */
export interface TextureDecode {
  lo: number
  hi: number
  levels: number
  nodata: number
}

/** The index a value quantises to, or `nodata`. The inverse of `decodeIndex`. */
export function decodeIndex(index: number, decode: TextureDecode): number | null {
  if (!Number.isFinite(index) || index === decode.nodata) return null
  const span = decode.hi - decode.lo || 1
  return decode.lo + (index / (decode.levels - 1)) * span
}

/**
 * Round half to **even**, which is numpy's rule and not JavaScript's.
 *
 * `palette_colors()` ends in `np.interp(...).round()`, and `np.round(0.5)` is
 * `0` where `Math.round(0.5)` is `1`. With integer stop colours and 255 evenly
 * spaced levels, an interpolated channel landing exactly on a half is
 * ordinary rather than exotic — so a ramp built with the wrong rule differs
 * from the one that coloured every frame on disk by one count on some entries.
 * Invisible on screen, and exactly the kind of drift the byte-for-byte fixture
 * exists to refuse.
 */
function roundHalfToEven(value: number): number {
  const floor = Math.floor(value)
  const frac = value - floor
  if (frac > 0.5) return floor + 1
  if (frac < 0.5) return floor
  return floor % 2 === 0 ? floor : floor + 1
}

/**
 * `np.interp`, in the one shape this file needs it: clamped at both ends,
 * never extrapolated.
 *
 * The clamp *is* the rule rather than a convenience — above the top stop keeps
 * the top stop's colour rather than running off into a colour the legend does
 * not show. `render.py::colorize` says the same thing in the same words.
 */
function interp(x: number, xs: readonly number[], ys: readonly number[]): number {
  if (x <= xs[0]) return ys[0]
  const last = xs.length - 1
  if (x >= xs[last]) return ys[last]
  let j = 0
  while (j < last - 1 && xs[j + 1] < x) j++
  const slope = (ys[j + 1] - ys[j]) / (xs[j + 1] - xs[j])
  return slope * (x - xs[j]) + ys[j]
}

/**
 * `(levels * 3)` RGB bytes: the variable's own ramp sampled across the range
 * the plate was quantised with.
 *
 * Uploaded as a `levels x 1` RGB texture with `LINEAR` filtering, sampled at
 * `(index + 0.5) / levels` so integer indices hit texel centres and anything
 * between them interpolates — which is what `np.interp` does on the Python
 * side, one stage earlier.
 *
 * A malformed stop is dropped rather than coalesced, the same rule the legend
 * and the SPC parser follow: a missing colour is never invented.
 */
export function buildRamp(
  colormap: readonly (readonly [number, string])[],
  lo: number,
  hi: number,
  levels: number,
): Uint8Array {
  const xs: number[] = []
  const channels: [number[], number[], number[]] = [[], [], []]
  for (const [value, color] of colormap) {
    if (!Number.isFinite(value) || typeof color !== 'string') continue
    if (!/^#[0-9a-f]{6}$/i.test(color)) continue
    xs.push(value)
    for (let c = 0; c < 3; c++) {
      channels[c].push(parseInt(color.slice(1 + c * 2, 3 + c * 2), 16))
    }
  }
  const out = new Uint8Array(Math.max(0, levels) * 3)
  if (xs.length === 0) return out

  // `np.linspace(lo, hi, levels)`: one step, computed once, with the last
  // entry forced to `hi` exactly — which is what numpy does and what keeps the
  // top of the ramp from landing a float ulp short of the top stop.
  const step = levels > 1 ? (hi - lo) / (levels - 1) : 0
  for (let i = 0; i < levels; i++) {
    const value = i === levels - 1 ? hi : lo + i * step
    for (let c = 0; c < 3; c++) {
      out[i * 3 + c] = roundHalfToEven(interp(value, xs, channels[c]))
    }
  }
  return out
}

/** The ramp for one catalog variable over one run's quantisation range. */
export function variableRamp(variable: ModelVariable, decode: TextureDecode): Uint8Array {
  return buildRamp(variable.colormap ?? [], decode.lo, decode.hi, decode.levels)
}
